import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Users, BadgeEuro, ShoppingCart, Landmark, MonitorPlay, UtensilsCrossed, Dumbbell, LineChart, Home, Plus } from "lucide-react";
import { Reveal } from "./Reveal";

/* ---------- Live-growing figures ---------- */
const BASELINE = new Date("2026-07-13T00:00:00Z");
const NOW = new Date();
const MONTHS_ELAPSED = Math.max(
  0,
  (NOW.getFullYear() - BASELINE.getFullYear()) * 12 + (NOW.getMonth() - BASELINE.getMonth())
);
const WEEKS_ELAPSED = Math.max(0, Math.floor((NOW - BASELINE) / (7 * 24 * 60 * 60 * 1000)));
const CA_VALUE = Math.round((1.3 + MONTHS_ELAPSED * 0.1) * 10) / 10;
const VIEWS_TOTAL = (250 + WEEKS_ELAPSED * 10) * 1_000_000;

// Detect small screens once at module load to skip heavy animations on phones.
const IS_MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

function CountUp({ target, format }) {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-60px" });
  const [n, setN] = useState(IS_MOBILE ? target : 0);
  useEffect(() => {
    // On mobile, skip the counter animation entirely — show final value.
    if (IS_MOBILE) {
      setN(target);
      return;
    }
    if (!inView) {
      setN(0);
      return;
    }
    let raf;
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min((now - t0) / 2000, 1);
      setN((1 - Math.pow(1 - p, 3)) * target);
      if (p < 1) raf = requestAnimationFrame(step);
      else setN(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);
  return <span ref={ref}>{format(n)}</span>;
}

/* ---------- Niches / secteurs couverts ---------- */
const NICHES = [
  { icon: ShoppingCart, label: "E-commerce" },
  { icon: MonitorPlay, label: "Infoproduit" },
  { icon: UtensilsCrossed, label: "Restauration" },
  { icon: Dumbbell, label: "Coach sportif" },
  { icon: LineChart, label: "Trading" },
  { icon: Home, label: "Agent immobilier" },
  { icon: Landmark, label: "Politique" },
  { icon: Plus, label: "+99 autres secteurs" },
];

export default function Stats() {
  return (
    <section data-testid="stats-section" className="mx-auto max-w-6xl px-6 py-24 text-center lg:py-32">
      <div className="relative isolate py-6">
        <Reveal>
          <p className="relative text-lg font-medium text-[#5B6B85]">
            Nos contenus ont généré plus de
          </p>
          <div
            data-testid="stat-views"
            className="font-head relative mt-4 text-6xl font-extrabold tracking-tight text-[#2563EB] sm:text-7xl lg:text-[8rem] lg:leading-[0.95]"
          >
            <CountUp
              target={VIEWS_TOTAL}
              format={(v) => Math.floor(v).toLocaleString("fr-FR").replace(/,/g, " ")}
            />
          </div>
          <p className="relative mt-5 text-base font-semibold uppercase tracking-[0.2em] text-[#94A3B8]">
            de vues cumulées
          </p>
        </Reveal>
      </div>

      <div className="mx-auto mt-16 grid max-w-3xl grid-cols-1 gap-8 sm:grid-cols-2">
        <Reveal delay={0.15}>
          <div className="flex flex-col items-center">
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-[#2563EB]/10 text-[#2563EB]">
              <Users size={20} />
            </span>
            <div className="font-head text-5xl font-extrabold text-[#0B1220]">
              <CountUp target={40} format={(v) => `+${Math.floor(v)}`} />
            </div>
            <p className="mt-2 text-base text-[#5B6B85]">
              <span className="font-semibold text-[#2563EB]">entrepreneurs</span> accompagnés
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="flex flex-col items-center">
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-[#2563EB]/10 text-[#2563EB]">
              <BadgeEuro size={20} />
            </span>
            <div className="font-head text-5xl font-extrabold text-[#0B1220]">
              <CountUp
                target={CA_VALUE}
                format={(v) => `${v.toFixed(1).replace(".", ",")}M€+`}
              />
            </div>
            <p className="mt-2 text-base text-[#5B6B85]">
              de <span className="font-semibold text-[#2563EB]">{"chiffre d'affaires"}</span> généré
            </p>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.25} className="mt-14">
        <p className="text-sm uppercase tracking-[0.2em] text-[#94A3B8]">
          Peu importe votre niche ou votre secteur.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-2.5">
          {NICHES.map(({ icon: Icon, label }, i) =>
            IS_MOBILE ? (
              <span
                key={i}
                data-testid={`niche-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E6EDF6] bg-white px-3 py-2 shadow-[0_6px_20px_-14px_rgba(15,23,42,0.3)] sm:gap-2 sm:px-3.5 sm:py-2.5"
              >
                <Icon size={15} className="shrink-0 text-[#2563EB]" />
                <span className="whitespace-nowrap text-[13px] font-semibold text-[#0B1220] sm:text-sm">{label}</span>
              </span>
            ) : (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`niche-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E6EDF6] bg-white px-3 py-2 shadow-[0_6px_20px_-14px_rgba(15,23,42,0.3)] transition-colors duration-300 hover:border-[#2563EB]/40 sm:gap-2 sm:px-3.5 sm:py-2.5"
              >
                <Icon size={15} className="shrink-0 text-[#2563EB]" />
                <span className="whitespace-nowrap text-[13px] font-semibold text-[#0B1220] sm:text-sm">{label}</span>
              </motion.span>
            )
          )}
        </div>
      </Reveal>
    </section>
  );
}
